/**
 * Keeping this screen's shapes in step with everyone else's (SRS C4, D1).
 *
 * The board hands over the whole list of objects whenever any of them changes, and
 * this works out what that means for the screen: what is new, what has moved, and
 * what has gone. A page carries tens of objects, so comparing the list costs nothing
 * and cannot drift out of step the way following a stream of changes can.
 *
 * Nothing here is in the way of the hand: a shape is on this board before any of it
 * runs (docs/03-ARCHITECTURE.md §7.2).
 */

import type { Layer, Unsubscribe } from '@opal/shared';

import type { ObjectBoard } from './object-board.js';
import type { BoardBridge } from './sync.js';

/** Joins the shapes on this device to the shared board. */
export function bindObjectsToBoard(
  objects: ObjectBoard,
  bridge: BoardBridge,
  /** SRS C8 — false for work on a layer this person may not see. */
  visible: (work: { readonly layer: Layer }) => boolean = () => true,
): Unsubscribe {
  return bridge.onObjectsChanged((all) => {
    const present = new Set<string>();
    // SRS C8 — a student's board is told about the lesson's work and nothing else, so
    // the teacher-only layer is not on this screen to be found by any means.
    const shared = all.filter(visible);

    for (const object of shared) {
      present.add(object.id);
      const here = objects.find(object.id);
      // Unchanged since this screen last saw it — including the shapes this screen
      // drew, which come straight back as they were sent.
      if (here?.updatedAt === object.updatedAt) continue;
      objects.put(object);
    }

    for (const here of [...objects.objects]) {
      if (!present.has(here.id)) objects.remove(here.id);
    }
  });
}
